import { Button, Popconfirm, Space, Switch, Tooltip, Typography, message } from "antd";
import { isDatasetGolden } from "common/goldenNominal";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { setDataSetRoleRequest, truncateDataSetRequest } from "store/goldenNominal/actions";

const UTILITY = "UTILITY";
// As dataset-service decides: a truncate whose heartbeat is older than this has stopped working
// (it no longer blocks anything, and a new truncate takes it over).
const TRUNCATE_STALE_MS = 20 * 60 * 1000;

function truncateRunning(truncate?: { status: string; heartbeatAt?: string; startedDate?: string } | null) {
    if (!truncate || truncate.status !== "RUNNING") {
        return false;
    }
    const heartbeat = Date.parse(truncate.heartbeatAt || truncate.startedDate || "");
    return !Number.isNaN(heartbeat) && heartbeat >= Date.now() - TRUNCATE_STALE_MS;
}

const HELP =
    "A utility source is a transient feed, not a system of record: what it contributes reaches the golden records through deduplication. Once that is done it can be truncated, and reloaded to refresh it.";

interface IDatasetUtilitySourceProps {
    businessAreaId?: string;
    schemaId?: string;
    dataSetId?: string;
    dataSetName?: string;
    datasetRole?: string | null;
    isGoldenNominal?: boolean;
    truncate?: { jobId: string; status: string; heartbeatAt?: string; startedDate?: string } | null;
    canEdit?: boolean;
    canDelete?: boolean;
}

// PRE-2797 — whether a dataset is a utility source, and truncating it (every record retired, the
// dataset taken out of every duplicate search). Only for a tenant with Golden Nominal maintenance
// on, and never for the golden dataset.
export default function DatasetUtilitySource({
    businessAreaId,
    schemaId,
    dataSetId,
    dataSetName,
    datasetRole,
    isGoldenNominal,
    truncate,
    canEdit,
    canDelete,
}: IDatasetUtilitySourceProps) {
    const dispatch = useAppDispatch();
    const goldenMaintenanceOn = useAppSelector(
        (state) => !!(state.userLogin.tenant as { features?: { goldenMaintenance?: boolean } } | undefined)?.features?.goldenMaintenance,
    );
    const goldenBySchema = useAppSelector((state) => state.goldenNominal?.goldenBySchema);
    const roleOverride = useAppSelector((state) => state.goldenNominal?.roleByDataSet?.[dataSetId || ""]);
    const setRoleResult = useAppSelector((state) => state.goldenNominal?.setRoleResult);
    const truncateResult = useAppSelector((state) => state.goldenNominal?.truncateResult);
    // The request in flight, with the result in the store when it was made (its answer is the next
    // one): neither control is used until it is answered, so a role change cannot race a truncate
    // this page started (the truncate's checks can take a few seconds).
    const [requesting, setRequesting] = React.useState<{ kind: "role" | "truncate"; before: unknown } | null>(null);
    const [startedJobId, setStartedJobId] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (requesting?.kind === "role" && setRoleResult && setRoleResult !== requesting.before) {
            setRequesting(null);
            if (setRoleResult.success) {
                message.success("Dataset role updated.");
            } else {
                message.error(setRoleResult.serverMessage || setRoleResult.message || "The dataset role could not be updated.");
            }
        }
    }, [setRoleResult, requesting]);

    React.useEffect(() => {
        if (requesting?.kind === "truncate" && truncateResult && truncateResult !== requesting.before) {
            setRequesting(null);
            if (truncateResult.success) {
                setStartedJobId(truncateResult.jobId || null);
                message.success("Truncate started. Follow it in Jobs; reload the dataset's data afterwards to refresh it.");
            } else {
                message.error(truncateResult.serverMessage || truncateResult.message || "The dataset could not be truncated.");
            }
        }
    }, [truncateResult, requesting]);

    if (!goldenMaintenanceOn || !dataSetId || isDatasetGolden(goldenBySchema, schemaId, dataSetId, isGoldenNominal)) {
        return null;
    }
    const isUtility = (roleOverride !== undefined ? roleOverride : datasetRole) === UTILITY;
    const truncating = !!startedJobId || requesting?.kind === "truncate" || truncateRunning(truncate);

    return (
        // Keeps its width in the dataset's button bar, as the buttons beside it do: squeezed, its
        // label would otherwise wrap a letter per line.
        <Space size="small" className="shrink-0 whitespace-nowrap" data-testid="dataset-utility-source">
            <Tooltip title={HELP}>
                <Space size={4}>
                    <Switch
                        size="small"
                        checked={isUtility}
                        disabled={!canEdit || truncating || !!requesting}
                        loading={requesting?.kind === "role"}
                        aria-label="Utility source"
                        onChange={(checked) => {
                            setRequesting({ kind: "role", before: setRoleResult });
                            dispatch(setDataSetRoleRequest(businessAreaId, schemaId, dataSetId, checked ? UTILITY : null));
                        }}
                        data-testid="dataset-utility-source-switch"
                    />
                    <Typography.Text>Utility source</Typography.Text>
                </Space>
            </Tooltip>
            {isUtility && (
                <Popconfirm
                    title={`Truncate ${dataSetName || "this dataset"}?`}
                    description={
                        <div className="max-w-sm">
                            Every record is retired: removed from the dataset (it can be restored) and taken out of every duplicate search. The golden records keep
                            what this source contributed, and a record that comes back later is matched to the same golden record. Meant for transient feeds,
                            once deduplication has applied them.
                        </div>
                    }
                    okText="Truncate"
                    okButtonProps={{ danger: true }}
                    disabled={!canDelete || truncating || !!requesting}
                    onConfirm={() => {
                        setRequesting({ kind: "truncate", before: truncateResult });
                        dispatch(truncateDataSetRequest(businessAreaId, schemaId, dataSetId));
                    }}
                >
                    <Button danger disabled={!canDelete || truncating || !!requesting} loading={requesting?.kind === "truncate"} data-testid="dataset-truncate-button">
                        {truncating ? "Truncating…" : "Truncate"}
                    </Button>
                </Popconfirm>
            )}
        </Space>
    );
}
